import { FinalStatus, IOrder, ISubOrder, PolicyInfoItem } from 'types/order';
import { getProp, propKey } from 'utils/prop';
import { PayType } from 'config/order';
import { fixDateStr, formatTimeStr } from 'utils/time';
import moment from 'moment';
import safeGet from 'lodash/get';

export const assertStype = (order: ISubOrder, stype: number) => {
    return getProp(order, ['extra', 'campaign', 'meta', 'stype']) === stype;
};

export const isInCampaign = (order: ISubOrder) => {
    return order.campaign_info && order.campaign_info.sale_price;
};

// 退货运费险标志是否展示
export const returnInsuranceObj = (order: IOrder | ISubOrder) => {
    let policyInfoArr = order.policy_info || []; // 保险数组
    return (
        policyInfoArr.find((item: PolicyInfoItem) => {
            return safeGet(item, 'policy_type', '') === 'RETURN_FREIGHT'; // 退货运费险
        }) || {
            ins_policy_no: '',
            status: 0,
            status_desc: '',
            policy_type: '',
            policy_detail: '',
            policy_amount: 0
        }
    );
};
// 是否是快闪店订单
export const isKsdOrder = (order: IOrder | ISubOrder) => {
    return assertStype(order, 8);
};

// 是否是拼团订单
export const isGroupBuy = (order: ISubOrder | ISubOrder) => {
    return assertStype(order, 2);
};

// 是否是f7项目的订单，小米快闪店活动
export const isF7Order = (order: IOrder | ISubOrder) => {
    const productId = order.product_id;

    return productId === '3321130738199486941' || productId === '3321129592097500461';
};

// 是否是f7项目的订单，vivo快闪店活动
export const isVoOrder = (order: IOrder | ISubOrder) => {
    const productId = order.product_id;

    return productId === '3330890571115030524';
};

// 是否是虚拟商品订单
export const isVirtualOrder = (order: ISubOrder | IOrder) => {
    return Number(getProp(order as Record<propKey, unknown>, ['extra', 'order_type'])) === 2;
};

export const canAftersaleOnFinish = (order: ISubOrder | IOrder) => {
    const afterSaleOnFinishPlatform = ['ez', 'ep'];
    // ez ep 商品不展示【申请售后】
    if (afterSaleOnFinishPlatform.indexOf(order.product_platform) >= 0) return false;
    // 虚拟商品订单已完成不展示【申请售后】
    if (isVirtualOrder(order) && order.final_status === 5) return false;
    return true;
};

/**
 * 是否是售后状态订单
 */
export const isAftersaleOrder = (order: IOrder) => {
    return (
        [
            FinalStatus.退货_在线退款成功,
            FinalStatus.退货_退款成功,
            FinalStatus.仅退款_成功,
            FinalStatus.仅退款_在线退款成功
        ].indexOf(order.final_status) !== -1
    );
};

/**
 * 换货状态订单
 * 40 - 售后换货-用户申请
 * 42 - 售后换货-用户填写退货物流
 * 43 - 售后换货-商家发货
 * 45 - 售后换货-商户审核（拒绝）
 * 46 - 售后换货-商户货物审核拒绝
 * 47 - 售后换货-用户申请仲裁
 * 50 - 售后换货-用户申请仲裁
 * 54 - 售后换货-客服仲裁（支持商户） / 售后换货-客服货物仲裁（支持商户）
 */
export const isExchangeOrder = (order: IOrder) => {
    return [40, 42, 43, 45, 46, 47, 50, 54].indexOf(order.final_status) !== -1;
};

// 是否是普通商品订单
export const isCommonOrder = (order: ISubOrder | IOrder) => {
    return Number(getProp(order as Record<propKey, unknown>, ['extra', 'order_type'])) === 0;
};

// 是否是在线支付订单
export const isOnlineOrder = (order: IOrder | ISubOrder) => {
    return order.pay_type === PayType.WECHAT || order.pay_type === PayType.ALIPAY;
};

// 是否是货到付款订单
export const isCodOrder = (order: IOrder | ISubOrder) => {
    return order.pay_type === PayType.COD;
};

// 是否可以申请退货，暂时是7天
export const inReturnGoodsTime = (order: IOrder | ISubOrder) => {
    const currentTime = new Date().getTime() / 1000;

    return currentTime - Number(order.update_time) < 604800;
};

// 是否是母订单
export const isMainOrder = (order: IOrder | ISubOrder) => {
    return Object.prototype.hasOwnProperty.call(order, 'sub_orders');
};

// 是否是预售
export function isPresale(order: ISubOrder): boolean {
    return getProp(order, ['extra', 'pre_sale_type']) === '1';
}

// 是否鲁班自己设置发货时间
export function isLubanShipTime(order: ISubOrder): boolean {
    let exp_time = 0;
    if (getProp(order, ['extra', 'exp_ship_time'])) {
        exp_time = order.extra.exp_ship_time * 1000;
    }
    const gap = exp_time - new Date(fixDateStr(order.create_time)).getTime();
    return (
        gap > 172800000 &&
        order.biz_type === 1 &&
        (order.final_status === 1 || order.final_status === 2)
    );
}

// 是否是卡券类订单
// 4: 抖音发码
// 5: 商家发码
export function isVoucherOrder(order: ISubOrder) {
    const orderType = Number(getProp(order, ['extra', 'order_type']));
    return orderType === 4 || orderType === 5;
}

// 获取总价
export function getAmount(order: ISubOrder): number {
    if (isInCampaign(order) && order.campaign_info) {
        return order.campaign_info.sale_price / 100;
    } else {
        return order.combo_amount / 100;
    }
}

// 获取预售时间
export function getPresaleTime(order: ISubOrder): string {
    if (!isPresale(order) && !isLubanShipTime(order)) {
        return '';
    }
    if (!order || !order.extra || !order.extra.exp_ship_time) {
        return '';
    }

    const fixedToHour = isLubanShipTime(order);
    let time = order.extra.exp_ship_time;
    if (time.toString().length === 10) {
        time = time * 1000;
        if (fixedToHour) {
            time += 3600000;
        }
    }
    let date = new Date(time);

    // 修正00：00为24：00
    if (fixedToHour && date.getHours() === 0) {
        time -= 86400000;
        date = new Date(time);

        return `${date.getMonth() + 1}月${date.getDate()}日24:00`;
    }

    return `${date.getMonth() + 1}月${date.getDate()}日${formatTimeStr(
        date.getHours()
    )}:${formatTimeStr(fixedToHour ? 0 : date.getMinutes())}${
        fixedToHour ? '' : `:${formatTimeStr(date.getSeconds())}`
    }`;
}

// 获取预售时间
export function getPresaleDate(order: ISubOrder): string {
    if ([1, 4, 2, 16, 17, 21, 25, 26].indexOf(order.final_status) === -1) {
        return '';
    }
    if (!isPresale(order) && !isLubanShipTime(order)) {
        return '';
    }
    if (!order || !order.extra || !order.extra.exp_ship_time) {
        return '';
    }

    let time = order.extra.exp_ship_time;
    if (time.toString().length === 10) {
        time = time * 1000;
    }
    // 发货时间往后数一天
    let date = new Date(time);

    return `${date.getMonth() + 1}月${date.getDate()}日`;
}

// 是否可以编辑地址 6小时
export const canEditAddressTime = (order: IOrder | ISubOrder) => {
    const currentTime = new Date().getTime() / 1000;

    return currentTime - Number(order.update_time) < 6 * 60 * 60;
};

export const isInOrderCenter = () => /\/views\/neworder\/center/.test(location.href);

/**
 * @description 判断玉石订单的状态
 * @param order 订单数据
 * @returns 订单状态：0普通订单逻辑，2待鉴定，3鉴定通过，4鉴定未通过，5已发货，6交易成功
 */
export const getYushiOrderStatus = (order: IOrder) => {
    const processType = order.service_process_type;
    const orderStatus = order.order_status;
    const processStatus = order.service_process_status;
    const processConclusion = order.service_process_conclusion;

    // 只有order_status为2/3/5的时候，玉石类订单和普通订单才有区别
    const isYushiDiff = orderStatus === 2 || orderStatus === 3 || orderStatus === 5;

    // 如果是非玉石订单，或者是玉石订单但和普通订单无区别，则返回0走普通订单逻辑
    if (processType !== 1 || !isYushiDiff) return 0;
    if (orderStatus === 3) return 5;
    if (orderStatus === 5) return 6;
    if (processStatus === 1) return 2;
    if (processStatus === 3 && processConclusion === 1) return 3;
    if (processStatus === 3 && processConclusion !== 1) return 4;

    return 0;
};

/**
 * 是否展示确认收货
 * @description 1、虚拟商品，展示确认收货
 *              2. 所有子单均满足：
 *                  a. 已发货状态 final_status = 3
 *                  b. 发货超过24小时、或者有物流信息
 * @param order
 */
export const canShowConfirm = (order: IOrder) => {
    if (isVirtualOrder(order)) return true; // 如果是虚拟商品，显示确认收货
    const isBefore24 = (time: number) => {
        if (!time) return false;
        return moment().isAfter(moment(time * 1000).add(24, 'h'));
    };
    // 检查每个商品单是否符合条件一
    // sub_orders 第一个对象为主单。构造过程见 services/v2/order/rebuild.ts rebuildSubOrders
    return order.sub_orders.every((o) => {
        let { final_status, logistics_time, logistics } = o;
        if (final_status !== 3) return false;
        // 存在物流信息 或者 发货时间超出24小时
        return !!logistics || isBefore24(logistics_time);
    });
};

/**
 * @description 是否是货到付款退款线上化订单，并且需要用户填写收款信息状态
 * @param order 订单数据
 */
export const isOnlineRefundOrderNeedPayee = (order: IOrder | ISubOrder) =>
    order.refund_type === 'ONLINE' && order.need_payee;

/**
 * @description 货到付款退款线上化订单退款失败状态
 * @param order 订单数据
 * @returns false|'MODIFY'|'SERVICE' false非退款失败，MODIFY修改收款信息，SERVICE联系官方客服
 */
export const isOnlineRefundFailOrder = (order: IOrder | ISubOrder) => {
    const { refund_status, need_modify_payee } = order;
    if (refund_status !== 4) return false;
    if (need_modify_payee) return 'MODIFY';
    if (!need_modify_payee) return 'SERVICE';
};

/**
 * 价格补足末尾00
 */
export const priceFormat = (price: number) => {
    if (!price) {
        return '0.00';
    }
    try {
        const [firstPrice, secondPrice] = String(price.toFixed(2)).split('.');
        return `${firstPrice}.${secondPrice.length >= 2 ? secondPrice : `${secondPrice}0`}`;
    } catch (error) {
        return price;
    }
};
